import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

export const PAGE_SIZE = 50;
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500];

/**
 * Hook to paginate any array client-side.
 * Returns the sliced page rows plus page controls. Users can change page size
 * via the dropdown rendered by <Pagination>. Auto-resets to page 1 whenever
 * the reset-key changes (typically the filter/search string) or when page size changes.
 */
export function usePagination(rows, defaultPageSize = PAGE_SIZE, resetKey = "") {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const total = rows?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => { setPage(1); }, [resetKey, total, pageSize]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (rows || []).slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  return { page, setPage, pageRows, totalPages, total, pageSize, setPageSize };
}

function buildPages(current, totalPages) {
  const pages = new Set([1, totalPages, current, current - 1, current + 1]);
  const list = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < list.length; i++) {
    out.push(list[i]);
    if (i < list.length - 1 && list[i + 1] - list[i] > 1) out.push("…");
  }
  return out;
}

/**
 * Pagination bar with page numbers, First/Prev/Next/Last buttons, and a
 * "Rows per page" selector (25 / 50 / 100 / 200 / 500).
 */
export default function Pagination({ page, setPage, totalPages, total, pageSize = PAGE_SIZE, setPageSize, testid = "pagination" }) {
  if (!total) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const pages = buildPages(page, totalPages);
  const btn = "h-8 min-w-[32px] px-2 rounded-md text-xs font-semibold border transition-colors";
  const idle = "bg-white border-slate-200 text-slate-700 hover:bg-slate-50";
  const active = "bg-[color:var(--brand-navy)] border-[color:var(--brand-navy)] text-white shadow-sm";
  const disabled = "opacity-40 pointer-events-none";

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 border-t border-slate-200 bg-slate-50" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
          Showing <span className="font-mono-num text-slate-800">{from}</span>–<span className="font-mono-num text-slate-800">{to}</span> of <span className="font-mono-num text-slate-800">{total}</span>
        </div>
        {setPageSize && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Rows</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="h-7 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 px-2 focus:outline-none focus:ring-1 focus:ring-[color:var(--brand-navy)]"
              data-testid={`${testid}-page-size`}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center gap-1" data-testid={`${testid}-controls`}>
          <button className={`${btn} ${page === 1 ? disabled : idle}`} onClick={() => setPage(1)} data-testid={`${testid}-first`} title="First page">
            <ChevronsLeft className="h-3.5 w-3.5" />
          </button>
          <button className={`${btn} ${page === 1 ? disabled : idle}`} onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`} title="Previous">
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          {pages.map((p, i) =>
            p === "…" ? (
              <span key={`e${i}`} className="px-1 text-slate-400 text-xs">…</span>
            ) : (
              <button
                key={p}
                className={`${btn} ${p === page ? active : idle}`}
                onClick={() => setPage(p)}
                data-testid={`${testid}-page-${p}`}
              >{p}</button>
            )
          )}
          <button className={`${btn} ${page === totalPages ? disabled : idle}`} onClick={() => setPage(page + 1)} data-testid={`${testid}-next`} title="Next">
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
          <button className={`${btn} ${page === totalPages ? disabled : idle}`} onClick={() => setPage(totalPages)} data-testid={`${testid}-last`} title="Last page">
            <ChevronsRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
